import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AddressAutocomplete from '../components/AddressAutocomplete';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { db, functions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import TopNav from '../components/TopNav';
import GradientButton from '../components/GradientButton';
import { colors, tierColors, tierEmoji } from '../theme';

const stripePromise = loadStripe('pk_live_51QyO4KGn2wy3V4TsGlvEECHkYdENkJbr7oH3sY18g6pPqq5ZXC7eCaQzlMR2WNVxWMjmAQC7IKkJ7TMLRK3Dv7800khf1RLTM');

const serviceCategories = [
  { id: 'wifi_networking', label: 'Wi-Fi & Networking', emoji: '📶' },
  { id: 'printer_peripherals', label: 'Printer & Peripherals', emoji: '🖨️' },
  { id: 'virus_removal', label: 'Virus Removal', emoji: '🦠' },
  { id: 'smart_home', label: 'Smart Home', emoji: '🏠' },
  { id: 'tv_device_setup', label: 'TV & Device Setup', emoji: '📺' },
  { id: 'social_media', label: 'Social Media', emoji: '📱' },
  { id: 'pc_setup', label: 'PC Setup', emoji: '💻' },
  { id: 'social_media_account_recovery', label: 'Account Recovery', emoji: '🔐' },
];

interface Tech {
  uid: string;
  displayName: string;
  tier: string;
  hourlyRate: number;
  rating: number;
  totalJobs: number;
  categories: string[];
  location?: { lat: number; lng: number; city: string; state: string };
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const STEPS = ['AI Chat', 'Address', 'Select Tech', 'Schedule', 'Payment'];

// ─── Stripe Payment Form ────────────────────────────────────────────────────
function StripePaymentForm({
  jobId,
  tech,
  plan,
  address,
  isAsap,
  onSuccess,
  onError,
}: {
  jobId: string;
  tech: Tech;
  plan: string;
  address: string;
  isAsap: boolean;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const serviceFee = plan === 'techpass' ? 0 : 9.99;
  const total = tech.hourlyRate + serviceFee;
  const tax = total * 0.08;

  const handlePay = async () => {
    if (!stripe || !elements || !user) return;
    setLoading(true);
    try {
      const createPI = httpsCallable(functions, 'createPaymentIntent');
      const result = await createPI({
        jobId,
        customerId: user.uid,
        technicianId: tech.uid,
        amountCents: Math.round(tech.hourlyRate * 100),
        serviceFeeCents: Math.round(serviceFee * 100),
        isTestMode: false,
      }) as any;

      const { clientSecret } = result.data;
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found');

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (error) {
        onError(error.message || 'Payment failed');
        return;
      }

      if (paymentIntent?.status === 'requires_capture' || paymentIntent?.status === 'succeeded') {
        // Dispatch the job
        const dispatchFn = httpsCallable(functions, 'dispatchJob');
        await dispatchFn({
          jobId,
          customerId: user.uid,
          category: tech.categories[0] || 'wifi_networking',
          preSelectedTechId: tech.uid,
          isEmergency: false,
        }).catch(() => {});
        onSuccess();
      }
    } catch (err: any) {
      onError(err.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Price breakdown */}
      <div
        style={{
          background: colors.surface,
          borderRadius: 14,
          padding: '16px 18px',
          marginBottom: 20,
        }}
      >
        <div style={{ fontWeight: 700, color: '#1A0A2E', fontSize: 15, marginBottom: 12 }}>
          Price Estimate
        </div>
        {[
          [`Hourly Rate`, `$${tech.hourlyRate}/hr`],
          [`Platform Fee`, plan === 'techpass' ? 'FREE ✓' : `$${serviceFee.toFixed(2)}`],
          [`Est. Tax (~8%)`, `$${tax.toFixed(2)}`],
        ].map(([label, value]) => (
          <div
            key={label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span style={{ color: colors.textSecondary, fontSize: 14 }}>{label}</span>
            <span
              style={{
                color: value === 'FREE ✓' ? colors.success : '#fff',
                fontWeight: value === 'FREE ✓' ? 700 : 400,
                fontSize: 14,
              }}
            >
              {value}
            </span>
          </div>
        ))}
        <div style={{ height: 1, background: colors.divider, margin: '10px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#1A0A2E', fontWeight: 700, fontSize: 15 }}>Est. Total (1hr)</span>
          <span style={{ color: colors.primary, fontWeight: 700, fontSize: 15 }}>
            ${(total + tax).toFixed(2)}
          </span>
        </div>
        <p style={{ color: colors.textSecondary, fontSize: 12, margin: '10px 0 0' }}>
          Your card is held, not charged until the job is complete.
        </p>
      </div>

      {/* Card input */}
      <div
        style={{
          background: colors.surface,
          borderRadius: 14,
          padding: '16px 18px',
          marginBottom: 20,
          border: '1.5px solid rgba(26,10,46,0.1)',
        }}
      >
        <div style={{ color: colors.textSecondary, fontSize: 13, marginBottom: 10 }}>Card Details</div>
        <CardElement
          options={{
            style: {
              base: {
                color: '#1A0A2E',
                fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                fontSize: '16px',
                '::placeholder': { color: 'rgba(26,10,46,0.35)' },
              },
              invalid: { color: '#FF453A' },
            },
          }}
        />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 16,
          justifyContent: 'center',
        }}
      >
        <span style={{ fontSize: 12 }}>🔒</span>
        <span style={{ color: colors.textSecondary, fontSize: 12 }}>
          Payments secured by Stripe
        </span>
      </div>

      <GradientButton onClick={handlePay} loading={loading} fullWidth style={{ fontSize: 16 }}>
        🔒 Authorize Card & Dispatch
      </GradientButton>
    </div>
  );
}

// ─── Main BookingPage ────────────────────────────────────────────────────────
export default function BookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile } = useAuth();
  const initialCategory = searchParams.get('category') || '';

  const [step, setStep] = useState(0); // 0=chat, 1=address, 2=tech, 3=schedule, 4=payment
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [issueDescription, setIssueDescription] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: `Hey! I'm Sage 👋 What's going on with your tech today?\n\nTell me what's happening and I'll try to help you fix it — and if we can't sort it out, I'll get a local technician to you fast.`,
    },
  ]);
  const [inputText, setInputText] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [techs, setTechs] = useState<Tech[]>([]);
  const [techsLoading, setTechsLoading] = useState(false);
  const [selectedTech, setSelectedTech] = useState<Tech | null>(null);
  const [isAsap, setIsAsap] = useState(true);
  const [scheduledDate, setScheduledDate] = useState('');
  const [address, setAddress] = useState(profile?.address || '');
  const [addressLat, setAddressLat] = useState<number | null>(null);
  const [addressLng, setAddressLng] = useState<number | null>(null);
  const [jobId, setJobId] = useState('');
  const [paymentDone, setPaymentDone] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [creatingJob, setCreatingJob] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  // Pre-populate address from profile
  useEffect(() => {
    if (profile?.address && !address) setAddress(profile.address);
  }, [profile]);

  const sendChatMessage = async () => {
    if (!inputText.trim()) return;
    const userMsg: ChatMessage = { role: 'user', content: inputText.trim() };
    setChatMessages((prev) => [...prev, userMsg]);
    const text = inputText.trim();
    setInputText('');
    setChatLoading(true);

    try {
      const geminiChatFn = httpsCallable(functions, 'geminiChat');

      // Build history in the exact format the Cloud Function expects:
      // Array of {role: "user"|"model", text: string} — NOT parts:[{text}]
      // Exclude the message we're about to send (it goes in `message` field separately)
      const history = chatMessages.map((m) => ({
        role: m.role === 'assistant' ? 'model' : 'user',
        text: m.content,
      }));

      const result = await geminiChatFn({
        history,
        message: text, // current message goes here, not in history
      }) as any;

      const data = result.data;
      // Function returns: { text, detectedCategory, isTriageComplete, shouldEscalate, isResolved, isEmergency }
      const reply = data.text || 'I can help you with that!';

      setChatMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
      setIssueDescription(text);

      if (data.detectedCategory && !selectedCategory) {
        setSelectedCategory(data.detectedCategory);
      }

      // Only move to address step when Sage explicitly escalates to a tech
      // (shouldEscalate, isEmergency, or isTriageComplete)
      if (data.shouldEscalate || data.isEmergency || data.isTriageComplete) {
        if (data.detectedCategory && !selectedCategory) {
          setSelectedCategory(data.detectedCategory);
        }
        // Add a bridging message then move to address
        setChatMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: "Let's get someone out to you. First — what address do you need help at? 📍",
          },
        ]);
        setTimeout(() => setStep(1), 1200);
      }
    } catch (err: any) {
      console.error('Gemini chat error:', err);
      // Don't auto-skip — show the error in chat so the user can still respond
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: "Sorry, I had a hiccup connecting to Sage AI. Can you try sending that again? 🙏",
        },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const loadTechs = async (category?: string) => {
    setTechsLoading(true);
    try {
      const q = query(
        collection(db, 'technicians'),
        where('isOnline', '==', true),
        where('isVerified', '==', true)
      );
      const snap = await getDocs(q);
      const results: Tech[] = [];
      snap.forEach((d) => {
        const data = d.data();
        if (
          !category ||
          (Array.isArray(data.categories) && data.categories.includes(category))
        ) {
          results.push({
            uid: d.id,
            displayName: data.displayName || 'Technician',
            tier: data.tier || 'bronze',
            hourlyRate: data.hourlyRate || 65,
            rating: data.rating || 4.8,
            totalJobs: data.totalJobs || 0,
            categories: data.categories || [],
            location: data.location,
          });
        }
      });

      // Sort: diamond > gold > silver > bronze
      const tierOrder: Record<string, number> = { diamond: 0, gold: 1, silver: 2, bronze: 3 };
      results.sort((a, b) => (tierOrder[a.tier] || 3) - (tierOrder[b.tier] || 3));
      setTechs(results.slice(0, 20));
    } catch (e) {
      console.error('Error loading techs:', e);
    } finally {
      setTechsLoading(false);
    }
  };

  const createJob = async () => {
    if (!user || !selectedTech) return;
    setCreatingJob(true);
    try {
      const jobRef = await addDoc(collection(db, 'jobs'), {
        customerId: user.uid,
        customerName: profile?.displayName || user.displayName || '',
        technicianId: selectedTech.uid,
        status: 'pending',
        jobType: isAsap ? 'asap' : 'scheduled',
        isEmergency: false,
        category: selectedCategory || selectedTech.categories[0] || 'wifi_networking',
        issueDescription: issueDescription || 'Tech support needed',
        serviceAddress: address,
        customerLocation: addressLat && addressLng ? {
          lat: addressLat,
          lng: addressLng,
          street: address,
        } : null,
        pricing: {
          hourlyRate: selectedTech.hourlyRate,
          serviceFee: profile?.plan === 'techpass' ? 0 : 9.99,
        },
        payment: { status: 'none' },
        timeline: { createdAt: serverTimestamp() },
        scheduledFor: !isAsap && scheduledDate ? new Date(scheduledDate) : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isTestMode: false,
      });
      setJobId(jobRef.id);
      setStep(4);
    } catch (e: any) {
      setPaymentError('Failed to create job. Please try again.');
    } finally {
      setCreatingJob(false);
    }
  };

  const handlePaymentSuccess = () => {
    setPaymentDone(true);
    setTimeout(() => navigate(`/job/${jobId}`), 1500);
  };

  if (paymentDone) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: colors.background,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          padding: 24,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 64, marginBottom: 20 }}>✅</div>
        <h2 style={{ color: '#1A0A2E', fontSize: 24, fontWeight: 700 }}>Booking Confirmed!</h2>
        <p style={{ color: colors.textSecondary }}>
          Your tech is on the way. Tracking your job...
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.background,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        paddingBottom: 40,
      }}
    >
      <TopNav title="Book a Technician" showBack />

      {/* Step indicator */}
      <div
        style={{
          display: 'flex',
          padding: '12px 16px',
          gap: 6,
          alignItems: 'center',
          overflowX: 'auto',
        }}
      >
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                opacity: i > step ? 0.4 : 1,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: i <= step ? colors.primary : 'rgba(26,10,46,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#1A0A2E',
                }}
              >
                {i < step ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: 10, color: i === step ? colors.primary : colors.textSecondary, whiteSpace: 'nowrap' }}>
                {s}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  background: i < step ? colors.primary : 'rgba(26,10,46,0.1)',
                  marginTop: -14,
                  minWidth: 16,
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      <div style={{ padding: '8px 16px' }}>
        {/* ── STEP 0: AI Chat ── */}
        {step === 0 && (
          <div>
            {/* Category picker */}
            {!selectedCategory && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ color: colors.textSecondary, fontSize: 13, marginBottom: 10 }}>
                  Optional: select a category first
                </p>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 8,
                  }}
                >
                  {serviceCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      style={{
                        background: colors.surface,
                        border: '1.5px solid rgba(26,10,46,0.06)',
                        borderRadius: 12,
                        padding: '10px 12px',
                        color: '#1A0A2E',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                      }}
                    >
                      <span>{cat.emoji}</span>
                      <span>{cat.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedCategory && (
              <div
                style={{
                  background: `rgba(102,51,153,0.2)`,
                  borderRadius: 10,
                  padding: '8px 12px',
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span style={{ color: '#1A0A2E', fontSize: 14 }}>
                  {serviceCategories.find((c) => c.id === selectedCategory)?.emoji}{' '}
                  {serviceCategories.find((c) => c.id === selectedCategory)?.label}
                </span>
                <button
                  onClick={() => setSelectedCategory('')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: colors.textSecondary,
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  ✕
                </button>
              </div>
            )}

            {/* Chat window */}
            <div
              style={{
                background: colors.surface,
                borderRadius: 16,
                padding: '16px',
                minHeight: 340,
                maxHeight: '55vh',
                overflowY: 'auto',
                marginBottom: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              {chatMessages.map((msg, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <div
                    style={{
                      maxWidth: '80%',
                      padding: '10px 14px',
                      borderRadius:
                        msg.role === 'user' ? '16px 16px 4px 16px' : '4px 16px 16px 16px',
                      background:
                        msg.role === 'user'
                          ? 'linear-gradient(135deg, #E040A0, #7B2FBE)'
                          : 'rgba(26,10,46,0.06)',
                      color: '#1A0A2E',
                      fontSize: 14,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {msg.role === 'assistant' && (
                      <div
                        style={{
                          fontSize: 11,
                          color: colors.primary,
                          fontWeight: 700,
                          marginBottom: 4,
                          letterSpacing: '0.05em',
                        }}
                      >
                        SAGE AI
                      </div>
                    )}
                    {msg.content}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div style={{ display: 'flex', gap: 5, padding: '8px 14px' }}>
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: colors.primary,
                        animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                      }}
                    />
                  ))}
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                placeholder="Describe your tech issue…"
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  background: colors.surface,
                  border: '1.5px solid rgba(26,10,46,0.12)',
                  borderRadius: 12,
                  color: '#1A0A2E',
                  fontSize: 15,
                  outline: 'none',
                  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                }}
              />
              <button
                onClick={sendChatMessage}
                disabled={chatLoading || !inputText.trim()}
                style={{
                  background: 'linear-gradient(135deg, #E040A0, #7B2FBE)',
                  border: 'none',
                  borderRadius: 12,
                  width: 48,
                  height: 48,
                  color: '#1A0A2E',
                  fontSize: 20,
                  cursor: chatLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: chatLoading || !inputText.trim() ? 0.5 : 1,
                }}
              >
                ↑
              </button>
            </div>


          </div>
        )}

        {/* ── STEP 1: Address ── */}
        {step === 1 && (
          <div>
            <h2 style={{ color: '#1A0A2E', fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
              📍 Where do you need help?
            </h2>
            <p style={{ color: colors.textSecondary, fontSize: 14, marginBottom: 20 }}>
              We'll show you available techs in your area
            </p>

            <AddressAutocomplete
              value={address}
              onChange={setAddress}
              onPlaceSelect={(place) => {
                setAddress(place.address);
                setAddressLat(place.lat);
                setAddressLng(place.lng);
              }}
              label="Service Address"
              style={{ marginBottom: 16 }}
              autoFocus
            />

            {profile?.address && profile.address !== address && (
              <button
                onClick={() => setAddress(profile.address!)}
                style={{
                  background: 'rgba(102,51,153,0.15)',
                  border: '1px solid rgba(102,51,153,0.3)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  color: colors.primary,
                  fontSize: 13,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 20,
                  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                  width: '100%',
                }}
              >
                🏠 Use saved home address: {profile.address}
              </button>
            )}

            <GradientButton
              onClick={async () => {
                await loadTechs(selectedCategory);
                setStep(2);
              }}
              disabled={!address.trim()}
              fullWidth
              style={{ marginTop: 8 }}
            >
              Find Techs Near Me →
            </GradientButton>
          </div>
        )}

        {/* ── STEP 2: Tech Selection ── */}
        {step === 2 && (
          <div>
            <h2 style={{ color: '#1A0A2E', fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
              Available Technicians
            </h2>
            <p style={{ color: colors.textSecondary, fontSize: 14, marginBottom: 16 }}>
              Verified, background-checked pros near you
            </p>
            <div style={{ background: 'rgba(102,51,153,0.1)', borderRadius: 10, padding: '8px 12px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13 }}>📍</span>
              <span style={{ color: colors.textSecondary, fontSize: 13 }}>{address}</span>
            </div>

            {techsLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    border: `3px solid ${colors.primary}`,
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.7s linear infinite',
                    margin: '0 auto 12px',
                  }}
                />
                <p style={{ color: colors.textSecondary }}>Finding techs near you…</p>
              </div>
            ) : techs.length === 0 ? (
              <div
                style={{
                  background: colors.surface,
                  borderRadius: 16,
                  padding: 32,
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
                <h3 style={{ color: '#1A0A2E', fontSize: 18 }}>No techs available right now</h3>
                <p style={{ color: colors.textSecondary, fontSize: 14 }}>
                  All techs in your area are currently busy. Try scheduling for later.
                </p>
                <GradientButton
                  onClick={() => loadTechs(selectedCategory)}
                  style={{ margin: '16px auto 0', padding: '12px 24px' }}
                >
                  Refresh
                </GradientButton>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {techs.map((tech) => (
                  <TechCard
                    key={tech.uid}
                    tech={tech}
                    onSelect={() => {
                      setSelectedTech(tech);
                      setStep(3);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── STEP 3: Schedule ── */}
        {step === 3 && selectedTech && (
          <div>
            <h2 style={{ color: '#1A0A2E', fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
              When do you need {selectedTech.displayName.split(' ')[0]}?
            </h2>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 24,
              }}
            >
              <span style={{ color: colors.success, fontSize: 13 }}>🕐</span>
              <span style={{ color: colors.success, fontSize: 13 }}>Typically arrives in 15–30 min</span>
            </div>

            {/* ASAP Option — PRIMARY */}
            <ScheduleOption
              icon="⚡"
              iconBg="rgba(255,140,0,0.15)"
              title="Come Now — ASAP"
              subtitle="Tech dispatched immediately"
              badge="Fastest"
              selected={isAsap}
              onClick={() => setIsAsap(true)}
            />

            {/* Scheduled Option */}
            <ScheduleOption
              icon="📅"
              iconBg="rgba(102,51,153,0.15)"
              title="Schedule for Later"
              subtitle={scheduledDate ? new Date(scheduledDate).toLocaleString() : 'Pick a date & time'}
              selected={!isAsap}
              onClick={() => setIsAsap(false)}
            />

            {!isAsap && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: colors.textSecondary, fontSize: 13, display: 'block', marginBottom: 8 }}>
                  Select date & time (min. 1 hour from now)
                </label>
                <input
                  type="datetime-local"
                  value={scheduledDate}
                  min={new Date(Date.now() + 3600000).toISOString().slice(0, 16)}
                  max={new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 16)}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '13px 14px',
                    background: colors.surface,
                    border: '1.5px solid rgba(26,10,46,0.1)',
                    borderRadius: 12,
                    color: '#1A0A2E',
                    fontSize: 15,
                    outline: 'none',
                    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                    boxSizing: 'border-box',
                    colorScheme: 'dark',
                  }}
                />
              </div>
            )}

            <GradientButton
              onClick={createJob}
              disabled={(!isAsap && !scheduledDate) || creatingJob}
              loading={creatingJob}
              fullWidth
              style={{ marginTop: 12 }}
            >
              {isAsap ? '⚡ Dispatch Now' : '📅 Schedule'}
            </GradientButton>
            {paymentError && (
              <p style={{ color: colors.error, fontSize: 14, marginTop: 10, textAlign: 'center' }}>{paymentError}</p>
            )}
          </div>
        )}

        {/* ── STEP 4: Payment ── */}
        {step === 4 && selectedTech && jobId && (
          <div>
            <h2 style={{ color: '#1A0A2E', fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
              Confirm & Pay
            </h2>

            {/* Tech summary */}
            <div
              style={{
                background: colors.surface,
                borderRadius: 14,
                padding: '14px 16px',
                marginBottom: 20,
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'rgba(102,51,153,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  fontWeight: 700,
                  color: colors.primary,
                  flexShrink: 0,
                }}
              >
                {selectedTech.displayName.charAt(0)}
              </div>
              <div>
                <div style={{ color: '#1A0A2E', fontWeight: 700, fontSize: 15 }}>
                  {selectedTech.displayName}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                  <TierBadge tier={selectedTech.tier} />
                  <span style={{ color: colors.textSecondary, fontSize: 13 }}>
                    ⭐ {selectedTech.rating.toFixed(1)}
                  </span>
                </div>
              </div>
              <div style={{ marginLeft: 'auto', color: colors.primary, fontWeight: 700, fontSize: 17 }}>
                ${selectedTech.hourlyRate}/hr
              </div>
            </div>

            <div
              style={{
                background: 'rgba(48,209,88,0.1)',
                border: '1px solid rgba(48,209,88,0.2)',
                borderRadius: 10,
                padding: '10px 14px',
                marginBottom: 20,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 14 }}>🔒</span>
              <span style={{ color: colors.success, fontSize: 13 }}>
                Card held, not charged until job is complete
              </span>
            </div>

            {paymentError && (
              <p style={{ color: colors.error, fontSize: 14, marginBottom: 12, textAlign: 'center' }}>
                {paymentError}
              </p>
            )}

            <Elements stripe={stripePromise}>
              <StripePaymentForm
                jobId={jobId}
                tech={selectedTech}
                plan={profile?.plan || 'free'}
                address={address}
                isAsap={isAsap}
                onSuccess={handlePaymentSuccess}
                onError={setPaymentError}
              />
            </Elements>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}

function TechCard({ tech, onSelect }: { tech: Tech; onSelect: () => void }) {
  return (
    <div
      style={{
        background: colors.surface,
        borderRadius: 16,
        overflow: 'hidden',
        border: '1px solid rgba(26,10,46,0.06)',
      }}
    >
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'rgba(102,51,153,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            fontWeight: 700,
            color: colors.primary,
            flexShrink: 0,
            position: 'relative',
          }}
        >
          {tech.displayName.charAt(0)}
          <span
            style={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              fontSize: 16,
            }}
          >
            {tierEmoji[tech.tier] || '🥉'}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#1A0A2E', fontWeight: 700, fontSize: 15 }}>{tech.displayName}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
            <TierBadge tier={tech.tier} />
            <span style={{ color: colors.textSecondary, fontSize: 12 }}>
              ⭐ {tech.rating.toFixed(1)}
            </span>
            <span style={{ color: colors.textSecondary, fontSize: 12 }}>
              · {tech.totalJobs} jobs
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 6,
              marginTop: 6,
              overflowX: 'auto',
              scrollbarWidth: 'none',
            }}
          >
            {tech.categories.slice(0, 3).map((cat) => (
              <span
                key={cat}
                style={{
                  background: 'rgba(102,51,153,0.15)',
                  color: colors.primary,
                  fontSize: 11,
                  padding: '3px 8px',
                  borderRadius: 20,
                  whiteSpace: 'nowrap',
                }}
              >
                {serviceCategories.find((c) => c.id === cat)?.label || cat}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div
        style={{
          borderTop: `1px solid ${colors.divider}`,
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <span style={{ color: colors.primary, fontWeight: 700, fontSize: 16 }}>
            ${tech.hourlyRate}/hr
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              background: 'rgba(48,209,88,0.1)',
              color: colors.success,
              fontSize: 12,
              padding: '4px 10px',
              borderRadius: 20,
              fontWeight: 600,
            }}
          >
            🕐 ~15 min
          </span>
          <GradientButton
            onClick={onSelect}
            style={{ padding: '8px 20px', fontSize: 14, borderRadius: 20 }}
          >
            Book
          </GradientButton>
        </div>
      </div>
    </div>
  );
}

function TierBadge({ tier }: { tier: string }) {
  const color = tierColors[tier] || tierColors.bronze;
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        fontSize: 11,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 20,
        textTransform: 'capitalize',
        border: `1px solid ${color}44`,
      }}
    >
      {tier}
    </span>
  );
}

function ScheduleOption({
  icon,
  iconBg,
  title,
  subtitle,
  badge,
  selected,
  onClick,
}: {
  icon: string;
  iconBg: string;
  title: string;
  subtitle: string;
  badge?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        background: colors.surface,
        border: `2px solid ${selected ? colors.primary : 'transparent'}`,
        borderRadius: 16,
        padding: '16px',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        cursor: 'pointer',
        boxShadow: selected ? `0 0 0 1px ${colors.primary}` : undefined,
        transition: 'border-color 0.15s',
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <span style={{ color: '#1A0A2E', fontWeight: 700, fontSize: 16 }}>{title}</span>
          {badge && (
            <span
              style={{
                background: colors.accent,
                color: '#1A0A2E',
                fontSize: 11,
                padding: '2px 8px',
                borderRadius: 20,
                fontWeight: 700,
              }}
            >
              {badge}
            </span>
          )}
        </div>
        <span style={{ color: colors.textSecondary, fontSize: 13 }}>{subtitle}</span>
      </div>
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: `2px solid ${selected ? colors.primary : 'rgba(26,10,46,0.12)'}`,
          background: selected ? colors.primary : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {selected && <span style={{ color: '#1A0A2E', fontSize: 12, lineHeight: 1 }}>✓</span>}
      </div>
    </div>
  );
}
